import { Stack, Typography } from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import BarChartIcon from "@mui/icons-material/BarChart";
import DeleteIcon from "@mui/icons-material/Delete";
import FileCopyIcon from "@mui/icons-material/FileCopy";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";
import ActionButton from "@quest_editor/components/Buttons/ActionButton";
import BackButton from "@quest_editor/components/Buttons/BackButton";
import DangerButton from "@quest_editor/components/Buttons/DangerButton";
import SubActionButton from "@quest_editor/components/Buttons/SubActionButton";
import Breadcrumb from "@quest_editor/components/Breadcrumb";
import PromotionTypeChip from "@quest_editor/components/Chips/PromotionTypeChip";
import PromotionStatusChip from "@quest_editor/components/Chips/PromotionStatusChip";
import { useQuest, usePromotion } from "@quest_editor/mocks";
import { getLinkParamOptions } from "@quest_editor/utils/view/constLabels";
import { getPromotionLabels } from "@quest_editor/utils/view/promotionLabels";
import { getBuildOptionsDisplay } from "@quest_editor/utils/view/buildOptionsDisplay";
import PromotionInfoSection from "./PromotionInfoSection";
import BasicInfoSection from "./BasicInfoSection";
import NoticeOptionSection from "./NoticeOptionSection";
import PhotoUploadOptionSection from "./PhotoUploadOptionSection";
import CheckInstructionSection from "./CheckInstructionSection";
import ForInHouseSection from "./ForInHouseSection";
import TargetSection from "./TargetSection";
import LinkSection from "./LinkSection";
import PurchaseSection from "./PurchaseSection";
import SurveySection from "./SurveySection";

const QuestDetail = () => {
  const { t } = useTranslation("questEditor");
  const { id: questId } = useParams<{ id: string }>();

  const { data: questData } = useQuest(questId);
  const quest = questData?.quest;

  const { data: promotionData } = usePromotion(quest?.promotionId);
  const promotion = promotionData?.promotion;

  const linkParams = getLinkParamOptions();
  const { getTypeLabel } = getPromotionLabels();
  const { buildOptionsDisplay } = getBuildOptionsDisplay();

  const getLinkParamLabel = (param: string) =>
    linkParams.find((lp) => lp.value === param)?.label ?? param;

  if (!quest || !promotion) {
    return <Typography>{t("common.label.not_found")}</Typography>;
  }

  return (
    <Stack direction="column" spacing={3} sx={{ flex: 1, minHeight: 0 }}>
      <Stack
        direction="row"
        alignItems="flex-start"
        justifyContent="space-between"
      >
        <Stack direction="column" spacing={2} alignItems="flex-start">
          <Breadcrumb
            items={[
              {
                label: promotion.name,
                to: `/quest_editor/promotion/${promotion.id}`,
              },
              { label: quest.name },
            ]}
          />
          <Stack direction="row" spacing={1}>
            <PromotionTypeChip type={promotion.type} />
            <PromotionStatusChip status={promotion.status} />
            <SubActionButton
              startIcon={<DownloadIcon />}
            >
              {t("quest_detail.button.data_download")}
            </SubActionButton>
            <SubActionButton
              startIcon={<BarChartIcon />}
              component={Link}
              to="analytics"
            >
              {t("quest_detail.button.analytics")}
            </SubActionButton>
          </Stack>
        </Stack>

        <Stack direction="row" spacing={1}>
          <SubActionButton
            startIcon={<FileCopyIcon />}
            component={Link}
            to="duplication"
          >
            {t("quest_detail.button.duplication")}
          </SubActionButton>
          <DangerButton
            startIcon={<DeleteIcon />}
          >
            {t("quest_detail.button.delete")}
          </DangerButton>
        </Stack>
      </Stack>

      <PromotionInfoSection
        questId={quest.id}
        promotion={promotion}
        getTypeLabel={getTypeLabel}
        buildOptionsDisplay={buildOptionsDisplay}
      />

      <BasicInfoSection quest={quest} />

      <NoticeOptionSection popupImageUrl={quest.popupImageUrl ?? null} />

      <PhotoUploadOptionSection
        productCheckNotice={quest.productCheckNotice ?? null}
      />

      <CheckInstructionSection
        checkerNote={quest.checkerNote ?? null}
        receiptCheckDescription={quest.receiptCheckDescription ?? null}
        checkerPhoneFile={quest.checkerPhoneFile ?? null}
      />

      <ForInHouseSection
        slackChannel={quest.slackChannel ?? null}
        memo={quest.memo ?? null}
      />

      <TargetSection targetClearCount={quest.targetClearCount} />

      <LinkSection
        linkTitle={quest.linkTitle ?? null}
        linkUrl={quest.linkUrl ?? null}
        linkParams={quest.linkParams ?? []}
        getLinkParamLabel={getLinkParamLabel}
      />

      <PurchaseSection />

      <SurveySection
        surveyPreInstruction={quest.surveyPreInstruction ?? null}
        qParam1Text={quest.qParam1Text ?? null}
        qParam2Text={quest.qParam2Text ?? null}
        qParam3Text={quest.qParam3Text ?? null}
        surveyId={quest.surveyId ?? null}
      />

      <Stack
        direction="row"
        justifyContent="center"
        alignItems="center"
        pt={2}
        sx={{ position: "relative" }}
      >
        <BackButton
          component={Link}
          to={`/quest_editor/promotion/${promotion.id}`}
          sx={{ position: "absolute", left: 0 }}
        >
          {t("quest_detail.button.back")}
        </BackButton>
        <Stack direction="row" spacing={2}>
          <SubActionButton
            size="small"
            component={Link}
            to={`/quest_editor/quest/preview/${quest.hash}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            {t("quest_detail.button.goto_preview")}
          </SubActionButton>
          <SubActionButton
            size="small"
            component={Link}
            to="start/request"
          >
            {t("quest_detail.button.goto_start_request")}
          </SubActionButton>
          <SubActionButton
            size="small"
            component={Link}
            to="start/preview"
          >
            {t("quest_detail.button.goto_start_review")}
          </SubActionButton>
          <ActionButton>
            {t("quest_detail.button.manual_stop")}
          </ActionButton>
        </Stack>
      </Stack>
    </Stack>
  );
};

export default QuestDetail;
